/**
 * useStaleSafeRequest — guard against race conditions on out-of-order async responses.
 *
 * Typical scenario (autocomplete, filter-driven search):
 *   user types "a"   → fetch #1 starts
 *   user types "ab"  → fetch #2 starts
 *   response #2 arrives first (fast) → UI assigns results for "ab"
 *   response #1 arrives later (slow) → UI OVERWRITES with stale results for "a"
 *
 * This helper issues a monotonic token on each `requestToken()` call. The caller
 * inspects `token.isLatest()` right before committing the response to reactive
 * state: if a newer request has started since, the response is ignored.
 *
 * The counter is scoped to the helper instance (closure), so two unrelated
 * composables do not interfere with each other.
 */
export interface StaleSafeToken {
	/** True iff no later token has been issued since this one. */
	isLatest(): boolean;
}

export interface UseStaleSafeRequest {
	/** Issue a new token. Any previously issued token becomes stale. */
	requestToken(): StaleSafeToken;
}

export function useStaleSafeRequest(): UseStaleSafeRequest {
	let seq = 0;

	return {
		requestToken(): StaleSafeToken {
			const mySeq = ++seq;
			return {
				isLatest: () => mySeq === seq,
			};
		},
	};
}
